// Payment and billing related types

export enum PaymentStatus {
  PENDING = 'pending',
  AUTHORIZED = 'authorized',
  CAPTURED = 'captured',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
  PARTIAL_REFUND = 'partial_refund'
}

export enum PaymentMethod {
  ONLINE = 'online',
  CARD = 'card',
  UPI = 'upi',
  NET_BANKING = 'net_banking',
  WALLET = 'wallet',
  BANK_TRANSFER = 'bank_transfer',
  CHEQUE = 'cheque',
  CASH = 'cash',
  RTGS = 'rtgs',
  NEFT = 'neft',
  CREDIT_TERMS = 'credit_terms'
}

export enum PaymentGateway {
  RAZORPAY = 'razorpay',
  MANUAL = 'manual'
}

export enum InvoiceStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  PARTIAL = 'partial',
  PAID = 'paid',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled'
}

export enum InvoiceType {
  B2B = 'b2b',
  B2C = 'b2c',
  EXPORT = 'export',
  SEZ = 'sez'
}

export interface Payment {
  id: string
  payment_id: string
  order_id: string
  customer_id: string
  amount: number
  currency: string
  status: PaymentStatus
  payment_method: PaymentMethod
  payment_gateway: PaymentGateway
  gateway_payment_id?: string
  gateway_order_id?: string
  reference_number?: string
  failure_reason?: string
  refund_amount?: number
  notes?: string
  created_at: string
  updated_at: string
  captured_at?: string
  failed_at?: string
  refunded_at?: string
  
  // Related data
  customer?: {
    id: string
    name: string
    email: string
  }
  order?: {
    id: string
    order_number: string
    total_amount: number
  }
}

export interface Invoice {
  id: string
  invoice_number: string
  order_id?: string
  customer_id: string
  customer_name?: string
  sales_order_id?: string
  sales_order_number?: string
  invoice_date: string
  due_date: string
  total_amount: number
  paid_amount: number
  outstanding_amount: number
  balance_amount: number  // Alias for outstanding_amount for backward compatibility
  status: InvoiceStatus
  invoice_type: InvoiceType
  supplier_gstin?: string
  buyer_gstin?: string
  total_taxable_value: number
  subtotal?: number
  cgst_amount: number
  sgst_amount: number
  utgst_amount: number
  igst_amount: number
  tax_amount: number
  pdf_url?: string
  notes?: string
  created_at: string
  updated_at: string
  
  // Related data
  customer?: {
    id: string
    name: string
    email: string
    phone?: string
    billing_name?: string
    contact_person?: string
    mobile_no?: string
  }
  order?: {
    id: string
    order_number: string
  }
  billing_address?: {
    address_line1: string
    address_line2?: string
    city: string
    state: string
    postal_code: string
    country?: string
  }
  shipping_address?: {
    address_line1: string
    address_line2?: string
    city: string
    state: string
    postal_code: string
    country?: string
  }
  line_items?: Array<{
    id?: string
    variant_id?: string
    product_name?: string
    variant_name?: string
    description?: string
    hsn_code?: string
    quantity: number
    unit_price: number
    discount_percent?: number
    discount_amount?: number
    taxable_amount?: number
    tax_rate?: number
    cgst_amount?: number
    sgst_amount?: number
    igst_amount?: number
    tax_amount?: number
    line_total?: number
  }>
  items?: Array<{
    id?: string
    variant_id?: string
    product_name?: string
    variant_name?: string
    description?: string
    hsn_code?: string
    quantity: number
    unit_price: number
    discount_percent?: number
    discount_amount?: number
    taxable_amount?: number
    tax_rate?: number
    cgst_amount?: number
    sgst_amount?: number
    igst_amount?: number
    tax_amount?: number
    line_total?: number
  }>
  payment?: Payment
}

export interface PaymentLink {
  id: string
  payment_link_id: string
  order_id: string
  customer_id: string
  amount: number
  currency: string
  description?: string
  short_url?: string
  status: string
  expire_by?: string
  created_at: string
  updated_at: string
}

export interface BillingDashboard {
  period: {
    start_date: string
    end_date: string
  }
  payment_statistics: {
    total_payments: number
    successful_payments: number
    failed_payments: number
    pending_payments: number
    total_amount: number
    successful_amount: number
    failed_amount: number
    pending_amount: number
    success_rate: number
  }
  invoice_statistics: {
    total_invoices: number
    total_invoice_amount: number
    paid_invoices: number
    paid_amount: number
    outstanding_amount: number
    overdue_invoices: number
    collection_rate: number
  }
  recent_activities: {
    recent_payments: Array<{
      id: string
      order_id: string
      customer_name: string
      amount: number
      status: string
      created_at: string
    }>
    recent_invoices: Array<{
      id: string
      invoice_number: string
      customer_name: string
      total_amount: number
      status: string
      created_at: string
    }>
  }
  top_customers: Array<{
    customer_id: string
    customer_name: string
    total_paid: number
    payment_count: number
  }>
}

export interface PaymentFilters {
  status?: PaymentStatus
  payment_method?: PaymentMethod
  payment_gateway?: PaymentGateway
  customer_id?: string
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
}

export interface InvoiceFilters {
  status?: InvoiceStatus
  customer_id?: string
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
}

export interface PaymentReconciliationRequest {
  start_date: string
  end_date: string
  payment_gateway?: PaymentGateway
}

export interface PaymentReconciliationResponse {
  period: {
    start_date: string
    end_date: string
  }
  summary: {
    total_orders: number
    reconciled_orders: number
    total_amount: number
    discrepancy_amount: number
  }
  discrepancies: Array<{
    order_id: string
    order_number: string
    discrepancy_type: string
    expected_amount: number
    actual_amount: number
    difference: number
    status: string
  }>
  orders_with_mismatched_status: Array<{
    order_id: string
    order_number: string
    local_status: string
    gateway_status: string
    amount: number
  }>
  orders_with_overpayment: Array<{
    order_id: string
    order_number: string
    expected_amount: number
    paid_amount: number
    overpayment: number
  }>
  orders_with_underpayment: Array<{
    order_id: string
    order_number: string
    expected_amount: number
    paid_amount: number
    underpayment: number
  }>
  metrics: {
    matched_payments: number
    unmatched_payments: number
    total_discrepancies: number
  }
}

export interface OfflinePaymentRequest {
  order_id: string
  amount: number
  payment_method: PaymentMethod
  reference_number: string
  notes?: string
}

export interface InvoiceCreateRequest {
  order_id: string
  invoice_date?: string
  due_date?: string
  invoice_type: InvoiceType
  supplier_gstin?: string
  buyer_gstin?: string
  notes?: string
}

export interface CustomerPaymentHistory {
  customer: {
    id: string
    name: string
    email: string
    customer_type?: string
  }
  payment_summary: {
    total_payments: number
    total_paid: number
    total_failed: number
    total_pending: number
    total_refunded: number
    outstanding_invoices: number
  }
  payments: Payment[]
  invoices: Invoice[]
  period: {
    start_date?: string
    end_date?: string
  }
}

export interface PaymentSummaryReport {
  report_data: Array<{
    period: string
    total_payments: number
    total_amount: number
    successful_payments: number
    successful_amount: number
    failed_payments: number
    failed_amount: number
    pending_payments: number
    pending_amount: number
  }>
  summary: {
    total_periods: number
    date_range: {
      start_date: string
      end_date: string
    }
    group_by: string
  }
}

export interface GSTValidationResponse {
  valid: boolean
  gstin?: string
  state_code?: string
  state_name?: string
  entity_code?: string
  check_digit?: string
  error?: string
}

export interface GSTRatesResponse {
  gst_rates: number[]
  state_codes: Record<string, string>
  invoice_types: Array<{
    value: string
    label: string
  }>
}